// Runtime (DOM + timeline) introspection of the composition's editable text. Drives both the
// co-visibility lint rule and the natural-text-editing preview/edit frame: it finds, per editable
// node, the window of timeline-time during which the node is actually visible, then intersects
// those windows to a single frame where every editable node is co-visible.
//
// It evaluates visibility only AT tween boundaries (not by dense sampling): GSAP exposes every
// tween's start/end, and between two adjacent boundaries each animated property is constant or
// monotonically eased, so a node's visibility crosses its threshold at most once per interval —
// the boundary samples reconstruct the (assumed-contiguous) visible window.

/** Effective opacity at or above this counts as "visible" — high enough to be a clean edit pose. */
const VISIBILITY_OPACITY_THRESHOLD = 0.5;

interface GsapAnimation {
    startTime(): number;
    endTime(): number;
    getChildren?(nested: boolean, tweens: boolean, timelines: boolean): GsapAnimation[];
}

/** The subset of the GSAP timeline API this module relies on. */
export interface GsapTimelineLike extends GsapAnimation {
    duration(): number;
    time(): number;
    seek(time: number, suppressEvents?: boolean): unknown;
    getChildren(nested: boolean, tweens: boolean, timelines: boolean): GsapAnimation[];
}

export interface EditableVisibility {
    hasEditableNodes: boolean;
    /** Timeline-time interval (seconds) where every editable node is co-visible; null if none. */
    sharedWindow: { start: number; end: number } | null;
    /** Normalized (0..1) midpoint of `sharedWindow`; null when there is no shared window. */
    editProgress: number | null;
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

/**
 * The sorted, de-duplicated set of timeline-times at which some tween starts or ends (plus the
 * timeline endpoints), expressed in root-timeline time. Recurses into nested timelines; assumes no
 * nested time-scaling (a documented v1 limitation).
 */
function collectBoundaryTimes(timeline: GsapTimelineLike, duration: number): number[] {
    const times = new Set<number>([0, duration]);
    const walk = (node: GsapTimelineLike, base: number): void => {
        for (const child of node.getChildren(false, true, true)) {
            const start = base + child.startTime();
            const end = base + child.endTime();
            times.add(clamp(start, 0, duration));
            times.add(clamp(end, 0, duration));
            if (typeof child.getChildren === 'function') {
                walk(child as GsapTimelineLike, start);
            }
        }
    };
    walk(timeline, 0);
    return Array.from(times).sort((a, b) => a - b);
}

/** Effective visibility of a node at the current timeline pose: opacity (incl. ancestors), display/visibility, and in-bounds. */
function isNodeVisible(node: Element, viewportWidth: number, viewportHeight: number): boolean {
    const view = node.ownerDocument.defaultView;
    if (!view) {
        return false;
    }
    let opacity = 1;
    let element: Element | null = node;
    while (element) {
        const style = view.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }
        opacity *= parseFloat(style.opacity || '1');
        element = element.parentElement;
    }
    if (opacity < VISIBILITY_OPACITY_THRESHOLD) {
        return false;
    }
    const rect = node.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
        return false;
    }
    return (
        rect.right > 0 && rect.left < viewportWidth && rect.bottom > 0 && rect.top < viewportHeight
    );
}

/**
 * Find each editable node's visible window by seeking the timeline to every boundary and measuring,
 * intersect them, and return the co-visible edit frame. Saves and restores the playhead so it does
 * not disturb the visible render.
 */
export function computeEditableVisibility(
    doc: Document,
    timeline: GsapTimelineLike,
): EditableVisibility {
    const nodes = Array.from(doc.querySelectorAll('[data-l5-editable]'));
    if (nodes.length === 0) {
        return { hasEditableNodes: false, sharedWindow: null, editProgress: null };
    }

    const duration = timeline.duration();
    const boundaries = collectBoundaryTimes(timeline, duration);
    const viewportWidth = doc.documentElement.clientWidth;
    const viewportHeight = doc.documentElement.clientHeight;
    const windows = nodes.map(() => ({ enter: Infinity, exit: -Infinity }));

    const originalTime = timeline.time();
    for (const time of boundaries) {
        timeline.seek(time, false);
        nodes.forEach((node, index) => {
            if (isNodeVisible(node, viewportWidth, viewportHeight)) {
                windows[index].enter = Math.min(windows[index].enter, time);
                windows[index].exit = Math.max(windows[index].exit, time);
            }
        });
    }
    timeline.seek(originalTime, true);

    if (windows.some((window) => window.enter === Infinity)) {
        return { hasEditableNodes: true, sharedWindow: null, editProgress: null };
    }
    const start = Math.max(...windows.map((window) => window.enter));
    const end = Math.min(...windows.map((window) => window.exit));
    if (start > end) {
        return { hasEditableNodes: true, sharedWindow: null, editProgress: null };
    }
    const midpoint = (start + end) / 2;
    return {
        hasEditableNodes: true,
        sharedWindow: { start, end },
        editProgress: duration > 0 ? clamp(midpoint / duration, 0, 1) : 0,
    };
}
